
import { Component, ComponentFactoryResolver, ComponentRef, Input, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { DxContextMenuComponent, DxDataGridComponent } from 'devextreme-angular';
import { GridExtender } from '../grid/Grid.extender';
import { BaseDataSourceAbstract } from '../datalayer/BaseDataSourceAbstract';
import { BaseState, BaseEntity } from './../entities/BaseEntities';
import { BaseComponent } from './BaseComponent';
import { ServiceBagService } from './ServiceBag';
import { BasePageComponent } from './BasePageComponent';
import { GridToolbarComponent } from '@m4u-angular/ui/layouts/componentlayouts/grid-toolbar/grid-toolbar.component';
import { AppInjector } from '@m4u-angular/core/injector/injector.module';
import dxButton from 'devextreme/ui/button';
import { Column } from 'devextreme/ui/data_grid';
import { ContextMenuElement } from '../classes/BaseClasses';
import { Subscription } from 'rxjs';
import { GridHelper } from '@m4u-angular/core/helpers/GridHelper';

@Component({
    selector: 'base-grid',
    template: '<div></div>'
})
// eslint-disable-next-line max-len
export abstract class BaseGridComponent<TEntity extends BaseEntity, TState extends BaseState> extends BaseComponent<TState> implements IGridComponent {
    private _gridToolbarRef: ComponentRef<GridToolbarComponent>;
    private _gridToolbarComponent: GridToolbarComponent;
    private _subscriptions: Subscription[] = [];

    private _dataSource: BaseDataSourceAbstract | any[];
    @Input() set DataSource(value: BaseDataSourceAbstract | any[]) {
        this._dataSource = value;
        if (value) this.InitDataSource();
    }
    get DataSource() {
        return this._dataSource;
    }

    private _showAddButton: boolean;
    @Input() set ShowAddButton(value: boolean) {
        this._showAddButton = value;
        this._gridToolbarComponent && (this._gridToolbarComponent.ShowAddButton = this._showAddButton);
    }
    get ShowAddButton() {
        return this._showAddButton;
    }

    private _addButtonText: string;
    @Input() set AddButtonText(value: string) {
        this._addButtonText = value;
        this._gridToolbarComponent && (this._gridToolbarComponent.AddButtonText = this._addButtonText);
    }
    get AddButtonText() {
        return this._addButtonText;
    }

    private _title: string;
    @Input() set Title(value: string) {
        this._title = value;
        this._gridToolbarComponent && (this._gridToolbarComponent.Title = this._title);
    }
    get Title() {
        return this._title;
    }

    private _addButtonNavParams: any;
    @Input() set AddButtonNavParams(value: any) {
        this._addButtonNavParams = value;
        this._gridToolbarComponent && (this._gridToolbarComponent.AddButtonNavParams = this._addButtonNavParams);
    }
    get AddButtonNavParams() {
        return this._addButtonNavParams;
    }

    @Input() set Visible(value: boolean) {
        this.grid && (this.grid.visible = value);
    }

    @Input() IsAutoHeight: boolean;
    @Input() Page: BasePageComponent<BaseEntity, BaseState>;
    @Input() AddButtonNavPath: string;
    @Input() StartExtraButtonsTemplate: TemplateRef<any>;
    @Input() EndExtraButtonsTemplate: TemplateRef<any>;

    @ViewChild(DxDataGridComponent) protected grid: DxDataGridComponent;
    @ViewChild(DxDataGridComponent, { read: ViewContainerRef }) containerRef: ViewContainerRef;

    public get gridAny(): any {
        return this.grid;
    }
    public get G(): GridExtender {
        return this.gridAny && this.gridAny.G;
    }

    private _isUpdateScrollPosition: boolean = false;

    protected AfterInit() { }

    constructor(public override A: ServiceBagService,
        public override ComponentName: string,
        public EntityType: (new () => TEntity),
        StateType: (new () => TState)) {
        super(A, ComponentName, StateType);
    }

    protected override OnInitComponent(): void {
        this.A.SysEvents.SetLoadPanelState(true);
        this._isUpdateScrollPosition = this.A.Screen.IsRTL();
        this.RegisterSubscription(this.A.SysEvents.MenuPositon.subscribe(() => this.SetScrollPosition()));
    }

    InitDataSource() {
        this.AfterInit();
    }

    override OnInitComponentFinished() {
        this._gridToolbarComponent = this.CreateGridToolbarComponent();

        this._subscriptions.push(this.grid.onContentReady.subscribe(() => {
            setTimeout(() => this.RefreshGridHeight());
            // this.SetScrollPosition();
            // Get context menu items without putting data there, just to know if any exists
            const contextMenuItems = this.GetGridRowContextMenu();
            // If no context menu items then no need to add column at all
            if (!contextMenuItems || !contextMenuItems.length) return;

            // TODO: better to use addColumn API method.
            const columns = this.grid.instance.option('columns');
            const gridColumns = columns.map((column: Column) => ({
                ...column,
                visible: column.type === 'buttons' ? column.visible : this.grid.instance.columnOption(column.dataField, 'visible'),
                width: column.type === 'buttons' ? column.width : this.grid.instance.columnOption(column.dataField, 'width'),
                visibleIndex: column.type === 'buttons' ? column.visibleIndex : this.grid.instance.columnOption(column.dataField, 'visibleIndex'),
                groupIndex: column.type === 'buttons' ? column.groupIndex : this.grid.instance.columnOption(column.dataField, 'groupIndex')
            }));

            const gridRowContextMenuColumn = gridColumns.find(x => x.name == 'gridRowContextMenu');
            if (!gridRowContextMenuColumn) {
                const columnOptions = {
                    name: 'gridRowContextMenu',
                    showInColumnChooser: false,
                    type: 'buttons',
                    cellTemplate: this.GridRowContextMenu,
                    search: {
                        enabled: false
                    },
                    cssClass: 'context-menu-column',
                    allowResizing: false,
                    allowReordering: false,
                    width: 32
                } as any;
                gridColumns.push(columnOptions);
                (this.grid.instance as any).option('columns', gridColumns);
            }
        }));

        //this._subscriptions.push(this.grid.onOptionChanged.subscribe((e) => {
        //    if (e.name !== 'dataSource') return;

        //    this.grid?.instance?.getScrollable()?.scrollTo({ top: 0 });
        //    this.grid?.instance?.repaint();
        //}));

        this.A.SysEvents.SetLoadPanelState(false);
    }

    GridRowContextMenu = (container, options) => {
        if (options.row.rowType !== 'data') return;

        const contextMenuItems = this.GetGridRowContextMenu(options);
        if (!contextMenuItems.length || !contextMenuItems.some(x => x.visible == null || x.visible)) return;

        const div = document.createElement('div');
        container.appendChild(div);

        const contextMenuButton = new dxButton(div, {
            icon: 'mdi mdi-dots-vertical',
            elementAttr: { class: 'grid-row-context-menu-button btn-size-small' },
            onClick: (event) => {
                event.event?.stopPropagation();
                const contextMenu = this.CreateContextMenuComponent();
                contextMenu.items = contextMenuItems.map(x => {
                    const visible = typeof x.visible == 'function' ? x.visible() : x.visible;
                    return {
                        ...x,
                        visible: visible
                    };
                });
                contextMenu.target = div;
                contextMenu.visible = true;
            }
        });
    };

    private CreateContextMenuComponent() {
        const componentFactoryResolver = AppInjector.get(ComponentFactoryResolver);
        const componentFactory = componentFactoryResolver.resolveComponentFactory(DxContextMenuComponent);
        const componentRef = this.containerRef.createComponent<DxContextMenuComponent>(componentFactory);

        return componentRef.instance;
    }

    // Please make sure that options?.row?.rowData or options?.data is using with '?' 
    // (for example, options?.row?.rowData?.EntryID) when overriding this method
    protected GetGridRowContextMenu(options?: any): ContextMenuElement[] {
        return [];
    }

    RefreshGridHeight() {
        if (this.IsAutoHeight) GridHelper.RefreshGridHeight(this.grid.instance, this.Page);
    }

    SetScrollPosition() {
        setTimeout(() => {
            if (!this._isUpdateScrollPosition) return;

            const scrollable = this.grid.instance.getScrollable();
            if (!scrollable) return;

            const maxScrollLeft = scrollable.scrollWidth();
            scrollable.scrollTo({
                x: maxScrollLeft
            });
        }, 150);
    }

    override Navigate(params: any[], optionalParams?: any, bSaveState = false, bSaveCurrentLocation = false): Promise<boolean> {
        if (this.Page != undefined) (this.Page as any).SaveState();
        return super.Navigate(params, optionalParams, bSaveState, bSaveCurrentLocation);
    }

    private CreateGridToolbarComponent(): GridToolbarComponent {
        if (!this.Title && !this.ShowAddButton && !this.StartExtraButtonsTemplate && !this.EndExtraButtonsTemplate) return;

        const previousElement = this.containerRef.element.nativeElement.parentElement.previousSibling;
        if (previousElement?.tagName?.toLowerCase() === 'm4u-grid-toolbar') previousElement.remove();

        const componentFactoryResolver = AppInjector.get(ComponentFactoryResolver);
        const componentFactory = componentFactoryResolver.resolveComponentFactory(GridToolbarComponent);
        const componentRef = this.containerRef.createComponent<GridToolbarComponent>(componentFactory);

        componentRef.instance.Grid = this.grid;
        componentRef.instance.ShowAddButton = this.ShowAddButton;
        componentRef.instance.Title = this.Title;
        componentRef.instance.AddButtonNavPath = this.AddButtonNavPath;
        componentRef.instance.AddButtonNavParams = this.AddButtonNavParams;
        componentRef.instance.StartExtraButtonsTemplate = this.StartExtraButtonsTemplate;
        componentRef.instance.EndExtraButtonsTemplate = this.EndExtraButtonsTemplate;
        componentRef.instance.AddButtonText = this.AddButtonText;

        this.containerRef.element.nativeElement.parentElement.before(componentRef.location.nativeElement);
        this._gridToolbarRef = componentRef;
        return componentRef.instance;
    }

    protected OnReorder(e, lines) {
        const visibleRows = e.component.getVisibleRows();
        const toIndex = lines.indexOf(visibleRows[e.toIndex].data);
        const fromIndex = lines.indexOf(e.itemData);
        lines.splice(fromIndex, 1);
        lines.splice(toIndex, 0, e.itemData);
    }

    protected override OnDestroyComponent(): void {
        this._subscriptions.forEach(x => x.unsubscribe());
        this._gridToolbarRef?.destroy();
    }
}


export interface IGridComponent {
    readonly G: GridExtender;
}
